import { Gauge } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { formatNumber } from "@/utils/format";
import type { VelocidadeVendasMesRow } from "./chartUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  rows: VelocidadeVendasMesRow[];
  velocidade: number;
}

export function VelocidadeVendasModal({ open, onClose, rows, velocidade }: Props) {
  const totais = rows.reduce(
    (acc, r) => {
      acc.vendasTotais += r.vendasTotais;
      acc.distratos += r.distratos;
      acc.vendasLiquidas += r.vendasLiquidas;
      return acc;
    },
    { vendasTotais: 0, distratos: 0, vendasLiquidas: 0 },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <Gauge className="h-4 w-4" />
          </div>
          <span className="text-sm font-semibold text-slate-800">
            Velocidade de vendas por mês
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl bg-indigo-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Velocidade</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-800">
              {formatNumber(velocidade, 2)}
            </p>
          </div>
          <div className="rounded-xl bg-emerald-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Vendas líquidas</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-emerald-700">
              {formatNumber(totais.vendasLiquidas)}
            </p>
          </div>
          <div className="rounded-xl bg-rose-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">Distratos</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-rose-700">
              {formatNumber(totais.distratos)}
            </p>
          </div>
          <div className="rounded-xl bg-red-50 p-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Vendas totais</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-red-700">
              {formatNumber(totais.vendasTotais)}
            </p>
          </div>
        </div>

        <p className="text-xs text-slate-500">
          Meses do ciclo da velocidade, inclusive sem venda. Líquidas = totais − distratos.
        </p>

        <div className="max-h-[55vh] overflow-auto rounded-xl border border-slate-100">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-100">
                <th className="px-3 py-2 text-left font-semibold text-slate-500">Mês</th>
                <th className="px-3 py-2 text-right font-semibold text-emerald-600">Vendas líquidas</th>
                <th className="px-3 py-2 text-right font-semibold text-rose-600">Distratos</th>
                <th className="px-3 py-2 text-right font-semibold text-red-600">Vendas totais</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.mesKey} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">{r.mes}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-emerald-600">
                    {formatNumber(r.vendasLiquidas)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-rose-600">
                    {formatNumber(r.distratos)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-red-600">
                    {formatNumber(r.vendasTotais)}
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="sticky bottom-0 bg-slate-50">
                <tr className="border-t border-slate-200">
                  <td className="px-3 py-2 font-semibold text-slate-700">
                    Total ({formatNumber(rows.length)} meses)
                  </td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums text-emerald-700">
                    {formatNumber(totais.vendasLiquidas)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums text-rose-700">
                    {formatNumber(totais.distratos)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums text-red-700">
                    {formatNumber(totais.vendasTotais)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          {rows.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-400">
              Sem vendas no ciclo para conferir.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
